import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  DictionaryAssetDeliveryStatus,
  DictionaryContractType,
  DictionaryContractItemServices,
  DictionaryAssetDocumentRequestTypes,
} from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { AssetAPIService } from 'src/app/extensions/services/api/asset-api.service';
import { FileStoreAPIService } from 'src/app/extensions/services/api/file-store-api.service';
import { ENV_CONFIG } from '../../../core/injection.token';

@Component({
  selector: 'asset-driver-detail',
  standalone: false,
  templateUrl: './asset-driver-detail.component.html',
  styleUrl: './asset-driver-detail.component.scss',
})
export class AssetDriverDetailComponent implements OnInit {
  public asset: app.models.AssetDetailModel;
  dataLoaded: boolean = false;
  lastInspectionDate: Date;

  AssetDeliveryStatus = DictionaryAssetDeliveryStatus;
  ContractType = DictionaryContractType;

  assetDocuments: string[] = ['VIC', 'MTPL Insurance', 'CASCO Insurance', 'PTI Certificate'];
  driverDocuments: string[] = ['Driving License', 'Personal ID Card'];

  contractItemServiceUrgentInDays: number;

  DictionaryContractItemServices = DictionaryContractItemServices;
  returnUrl: string | null = null;
  isFleetManagement = false;
  assetDetail: any;

  public constructor(
    private _assetAPIService: AssetAPIService,
    private _activatedRoute: ActivatedRoute,
    private _fileStoreAPIService: FileStoreAPIService,
    private _router: Router,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {
    this.contractItemServiceUrgentInDays = Number(this._envConfig.CONTRACT_ITEM_SERVICE_URGENT_IN_DAYS) || 14;
  }

  public ngOnInit(): void {
    this.returnUrl = this._activatedRoute.snapshot.queryParamMap.get('returnUrl');

    if (this.returnUrl) {
      this.returnUrl = this.returnUrl.includes('/') ? this.returnUrl.split('/')[1] : this.returnUrl;
      this.isFleetManagement = this.returnUrl === 'asset-fleet-management-list';
      if (this.isFleetManagement) {
        this.assetDocuments = ['VIC', 'Premium Insurnace', 'PTI Certificate'];
        this.driverDocuments = ['Driving License'];
      }
    }

    this.assetDetail = JSON.parse(localStorage.getItem('SELECTED_ASSET')!);

    this.loadData();
  }

  loadData() {
    const assetId = this._activatedRoute.snapshot.params['id'];
    this._assetAPIService.getAssetDetails(assetId).subscribe(data => {
      this.asset = data;
      this._assetAPIService.getServiceAppointments(this.asset.assetId).subscribe(data => {
        this.asset.contractItemServices.forEach(x => {
          x.historyServices = data
            .filter(y => y.serviceCode == x.code)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

          x.startWith = 0;
          x.showHistoryServices = x.historyServices.slice(0, 2);

          if (x.code == DictionaryContractItemServices.MAINTENANCE) {
            this.lastInspectionDate = x.historyServices[0]?.date;
          }
        });
      });

      this.asset.mileage = this.assetDetail.mileage;
      if (this.isFleetManagement) {
        this.asset.driverClass = this.assetDetail.driverClass;
        this.asset.servicePartner = 'BMW Neuper-Judenburg';
        this.asset.availableService = 'Financial Leasing';
      }

      this.dataLoaded = true;
    });
  }

  backHandler() {
    if (this.returnUrl) {
      this._router.navigate([decodeURIComponent(this.returnUrl)]);
    } else {
      this._router.navigate(['asset-driver-list']);
    }
  }

  redirectDocumentRequest(contractId: string, assetId: string) {
    this._router.navigate(['requests/new', contractId, assetId]);
  }

  redirectAssetJourney(assetId: string) {
    this._router.navigate([this.returnUrl ? this.returnUrl : 'asset-driver-list', 'journey', assetId], {
      queryParams: { returnUrl: this._router.url.split('?')[0] },
    });
  }

  redirectReportIncident(contractId: string, assetId: string) {
    this._router.navigate(['requests/new', contractId, assetId, DictionaryAssetDocumentRequestTypes.DAMAGE_DELEGACY]);
  }

  downloadDocument(documentName: string): void {
    if (documentName) {
      this._fileStoreAPIService.getFile(documentName).subscribe(response => {
        if (response) {
          const extension = documentName.split('.').pop()?.toLowerCase();
          const documentRef = `data:application/${extension};base64,${response}`;
          const downloadLink = document.createElement('a');

          downloadLink.href = documentRef;
          downloadLink.download = documentName;
          downloadLink.click();
        }
      });
    }
  }

  getFileTypeIcon(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'file-pdf';
      case 'doc':
      case 'docx':
        return 'file-word';
      default:
        return 'file-pdf';
    }
  }

  getDaysBetween(date1: Date | string, date2: Date | string): number {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    const diffMs = d1.getTime() - d2.getTime(); // milliseconds
    return Math.floor(diffMs / (1000 * 60 * 60 * 24)); // convert to days
  }

  get utcToday(): Date {
    return new Date();
  }

  bookAppointmentHandler(serviceCode: string, serviceName: string) {
    if (!this.asset.assetId) return;

    //TBD: replace assetName from dictionary identification
    const serviceAppointmentBaseObject = {
      serviceCode: serviceCode,
      serviceName: serviceName,
      registrationNumber: this.asset.registrationNumber,
    };
    localStorage.setItem('SERVICE_APPOINTMENT', JSON.stringify(serviceAppointmentBaseObject));
    if (this.returnUrl) {
      this._router.navigate(['asset/serviceAppointment/timeslot', this.asset.assetId], {
        queryParams: { returnUrl: this._router.url.split('?')[0] },
      });
    } else {
      this._router.navigate(['asset/serviceAppointment/timeslot', this.asset.assetId]);
    }
  }

  getMaxDateByHistoryServices(historyServices: app.models.HistoryServiceModel[]) {
    if (!historyServices || historyServices.length === 0) {
      return null;
    }

    return new Date(Math.max(...historyServices.map(x => new Date(x.date).getTime())));
  }

  previousHistoryService(contractItemService: app.models.ContractItemServiceModel) {
    if (contractItemService.startWith === 0) {
      return;
    }

    contractItemService.startWith = contractItemService.startWith - 1;
    contractItemService.showHistoryServices = contractItemService.historyServices.slice(
      contractItemService.startWith,
      contractItemService.startWith + 2
    );
  }
  nextHistoryService(contractItemService: app.models.ContractItemServiceModel) {
    if (contractItemService.startWith + 2 >= contractItemService.historyServices.length) {
      return;
    }

    contractItemService.startWith = contractItemService.startWith + 1;
    contractItemService.showHistoryServices = contractItemService.historyServices.slice(
      contractItemService.startWith,
      contractItemService.startWith + 2
    );
  }
}
